import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { WorkflowScopes, StatusCodes } from '../constants/workflows';
import { useStatusTransitions } from '../hooks/useStatusTransitions';
import { Button } from './button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from './dialog';
import { DropdownSelect } from './dropdown-select';
import { FieldValidationErrorMessage } from './field-validation-error-message';
import { Fieldset, Field, Label, Description } from './fieldset';
import { Input } from './input';
import { Textarea } from './textarea';

const statusModalFormSchema = z.object({
  current_status: z.string().nullable().optional(),
  current_reason: z.string().nullable().optional(),
  comment: z.string().optional(),
  next_review_on: z.string().optional(),
});

type StatusModalFormData = z.infer<typeof statusModalFormSchema>;

export interface StatusTransitionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: {
    statusCode: string | null;
    reasonCode: string | null;
    comment?: string;
    next_review_on?: string | null;
  }) => void;
  initialStatusCode: string | null;
  targetStatusCode?: string | null;
  workflowScope: string;
  isPending?: boolean;
  currentReasonCode?: string | null;
  currentStatusTitle?: string | null;
}

export function StatusTransitionModal({
  open,
  onClose,
  onConfirm,
  initialStatusCode,
  targetStatusCode,
  workflowScope,
  isPending = false,
  currentReasonCode = null,
  currentStatusTitle = null,
}: StatusTransitionModalProps) {
  const { availableTransitions, getStatusConfiguration, statuses } = useStatusTransitions({
    currentStatus: initialStatusCode,
    workflowScope,
  });

  const statusModalForm = useForm<StatusModalFormData>({
    resolver: zodResolver(statusModalFormSchema),
    defaultValues: {
      current_status: initialStatusCode,
      current_reason: currentReasonCode,
      comment: '',
      next_review_on: '',
    },
    mode: 'onChange',
  });

  const {
    control: statusModalControl,
    handleSubmit: statusModalHandleSubmit,
    watch: statusModalWatch,
    setValue: statusModalSetValue,
    setError: statusModalSetError,
    reset: statusModalReset,
    formState: { errors: statusModalErrors, isSubmitting: statusModalIsSubmitting },
  } = statusModalForm;

  const statusModalWatchedCurrentStatus = statusModalWatch('current_status');
  const statusModalWatchedCurrentReason = statusModalWatch('current_reason');

  // Track the previous status for modal form
  const [previousStatusModal, setPreviousStatusModal] = useState<string | null>(initialStatusCode);

  // Reset form when modal opens or initial status changes
  useEffect(() => {
    if (open) {
      const initialStatus = targetStatusCode || initialStatusCode;
      statusModalReset({
        current_status: initialStatus,
        current_reason: currentReasonCode,
        comment: '',
        next_review_on: '',
      });
      setPreviousStatusModal(initialStatus);
    }
  }, [open, initialStatusCode, targetStatusCode, currentReasonCode, statusModalReset]);

  const showNextReviewDate =
    workflowScope === WorkflowScopes.Clearance &&
    statusModalWatchedCurrentStatus === StatusCodes.NOT_CLEARED;

  // Status modal form requirements
  const statusModalCurrentStatusRequirements = useMemo(() => {
    if (!statusModalWatchedCurrentStatus) {
      return { requiresReason: false, requiresNote: false, availableReasons: [] };
    }
    const statusConfiguration = getStatusConfiguration(statusModalWatchedCurrentStatus);
    return {
      requiresReason: statusConfiguration.require_reason,
      requiresNote: statusConfiguration.require_note,
      availableReasons: statusConfiguration.reasons || [],
    };
  }, [statusModalWatchedCurrentStatus, getStatusConfiguration]);

  // Status modal form computed values
  const statusModalStatusHasChanged = useMemo(() => {
    const watchedStatus = statusModalWatchedCurrentStatus ?? null;
    const needStatus = initialStatusCode ?? null;
    return watchedStatus !== needStatus;
  }, [statusModalWatchedCurrentStatus, initialStatusCode]);

  const statusModalWorkflowHasChanged = useMemo(() => {
    const watchedReason = statusModalWatchedCurrentReason ?? null;
    const needReason = currentReasonCode ?? null;
    const reasonHasChanged = watchedReason !== needReason;
    return statusModalStatusHasChanged || reasonHasChanged;
  }, [statusModalStatusHasChanged, statusModalWatchedCurrentReason, currentReasonCode]);

  const statusModalCommentIsRequired = useMemo(() => {
    return statusModalStatusHasChanged && statusModalCurrentStatusRequirements.requiresNote;
  }, [statusModalStatusHasChanged, statusModalCurrentStatusRequirements.requiresNote]);

  const statusReasonLabel =
    workflowScope === 'staffing.need' && statusModalWatchedCurrentStatus !== 'CLOSED'
      ? 'Labor Tier'
      : 'Reason';

  const statusModalReasonIsRequired = useMemo(() => {
    return statusModalStatusHasChanged && statusModalCurrentStatusRequirements.requiresReason;
  }, [statusModalStatusHasChanged, statusModalCurrentStatusRequirements.requiresReason]);

  // Guard against a misconfigured workflow: a status that requires a reason but
  // exposes no reason options for the current audience (e.g. a staff-settable
  // status whose reasons are all provider-only). Without this, the reason field
  // is never rendered (so its validation error stays hidden) and Save would
  // silently no-op when the submit handler bails on the missing-reason check.
  const statusModalReasonRequiredButUnavailable = useMemo(
    () =>
      statusModalReasonIsRequired &&
      statusModalCurrentStatusRequirements.availableReasons.length === 0,
    [statusModalReasonIsRequired, statusModalCurrentStatusRequirements.availableReasons.length]
  );

  const statusOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];

    // Add current status if we have it
    if (initialStatusCode && statuses[initialStatusCode]) {
      options.push({
        value: initialStatusCode,
        label: statuses[initialStatusCode].title,
      });
    }

    // Add available transitions
    availableTransitions.forEach((status) => {
      options.push({
        value: status.code,
        label: status.title,
      });
    });

    return options;
  }, [initialStatusCode, statuses, availableTransitions]);

  const statusModalReasonOptions = useMemo(() => {
    return statusModalCurrentStatusRequirements.availableReasons.map((reason) => ({
      value: reason.code,
      label: reason.title,
    }));
  }, [statusModalCurrentStatusRequirements.availableReasons]);

  // Clear reason when status changes or if it's not valid for the current status
  useEffect(() => {
    // Update previous status tracker
    if (statusModalWatchedCurrentStatus !== previousStatusModal) {
      setPreviousStatusModal(statusModalWatchedCurrentStatus ?? null);
    }

    if (!statusModalWatchedCurrentStatus) {
      // If status is cleared, clear reason too
      if (statusModalWatchedCurrentReason) {
        statusModalSetValue('current_reason', null);
      }
      return;
    }

    // If status changed, clear reason to force user to select a new one
    const statusChanged =
      previousStatusModal !== null && statusModalWatchedCurrentStatus !== previousStatusModal;
    if (statusChanged && statusModalWatchedCurrentReason) {
      statusModalSetValue('current_reason', null);
      return;
    }

    // If there's no reason set, check if we need to validate
    if (!statusModalWatchedCurrentReason) return;

    const availableReasonCodes = statusModalCurrentStatusRequirements.availableReasons.map(
      (r) => r.code
    );

    // Clear reason if: (1) status has no reasons, OR (2) current reason is not in the available list
    const shouldClearReason =
      availableReasonCodes.length === 0 ||
      !availableReasonCodes.includes(statusModalWatchedCurrentReason);

    if (shouldClearReason) {
      statusModalSetValue('current_reason', null);
    }
  }, [
    statusModalWatchedCurrentStatus,
    statusModalWatchedCurrentReason,
    previousStatusModal,
    statusModalCurrentStatusRequirements,
    statusModalSetValue,
  ]);

  // Status modal form submit handler
  const onStatusModalSubmit = statusModalHandleSubmit(async (formData) => {
    try {
      // Validate reason if required
      if (statusModalReasonIsRequired && !formData.current_reason) {
        statusModalSetError('current_reason', {
          type: 'manual',
          message: 'Reason is required for this status',
        });
        return;
      }

      // Validate comment if required
      if (statusModalCommentIsRequired && !formData.comment?.trim()) {
        statusModalSetError('comment', {
          type: 'manual',
          message: 'Note is required for this status',
        });
        return;
      }

      // Call onConfirm with the form data
      onConfirm({
        statusCode: formData.current_status || null,
        reasonCode: formData.current_reason || null,
        comment: formData.comment?.trim(),
        next_review_on: formData.next_review_on?.trim() || null,
      });
    } catch (error) {
      // Errors are handled by parent component
    }
  });

  const handleStatusModalClose = () => {
    onClose();
    // Reset form to initial values
    statusModalReset({
      current_status: initialStatusCode,
      current_reason: currentReasonCode,
      comment: '',
      next_review_on: '',
    });
    setPreviousStatusModal(initialStatusCode);
  };

  return (
    <Dialog open={open} onClose={handleStatusModalClose}>
      <DialogTitle>Change Status</DialogTitle>
      <DialogBody>
        <form onSubmit={onStatusModalSubmit} className="tw:space-y-4">
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:gap-4">
              <Field>
                <Label>Status</Label>
                <Description>
                  {currentStatusTitle || initialStatusCode
                    ? `Current: ${currentStatusTitle || statuses[initialStatusCode || '']?.title || 'Unknown'}`
                    : 'Select a status'}
                </Description>
                <Controller
                  name="current_status"
                  control={statusModalControl}
                  render={({ field }) => (
                    <DropdownSelect
                      allOptions={statusOptions}
                      value={field.value}
                      onChange={(selectedOption) => {
                        if (!selectedOption || Array.isArray(selectedOption)) {
                          field.onChange(null);
                          return;
                        }
                        field.onChange(selectedOption.value || null);
                      }}
                      placeholder="Select status..."
                    />
                  )}
                />
                {statusModalErrors.current_status && (
                  <FieldValidationErrorMessage message={statusModalErrors.current_status.message} />
                )}
              </Field>

              {statusModalCurrentStatusRequirements.availableReasons.length > 0 && (
                <Field>
                  <Label>
                    {statusReasonLabel}
                    {statusModalCurrentStatusRequirements.requiresReason && (
                      <span className="tw:text-stoplight-danger">*</span>
                    )}
                  </Label>
                  <Description>
                    {statusModalCurrentStatusRequirements.requiresReason
                      ? 'Required for this status'
                      : 'Optional'}
                  </Description>
                  <Controller
                    name="current_reason"
                    control={statusModalControl}
                    render={({ field }) => (
                      <DropdownSelect
                        allOptions={statusModalReasonOptions}
                        value={field.value}
                        onChange={(selectedOption) => {
                          if (!selectedOption || Array.isArray(selectedOption)) {
                            field.onChange(null);
                            return;
                          }
                          field.onChange(selectedOption.value || null);
                        }}
                        placeholder="Select reason..."
                      />
                    )}
                  />
                  {statusModalErrors.current_reason && (
                    <FieldValidationErrorMessage
                      message={statusModalErrors.current_reason.message}
                    />
                  )}
                </Field>
              )}

              {statusModalReasonRequiredButUnavailable && (
                <FieldValidationErrorMessage message="This status requires a reason, but no reason options are available for your role. Please contact support if you need to make this change." />
              )}

              {showNextReviewDate && (
                <Field>
                  <Label>Next Review Date</Label>
                  <Description>When is this provider re-eligible for name clearance?</Description>
                  <Controller
                    name="next_review_on"
                    control={statusModalControl}
                    render={({ field }) => (
                      <Input
                        type="date"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value || '')}
                      />
                    )}
                  />
                </Field>
              )}

              {statusModalWorkflowHasChanged && (
                <Field>
                  <Label>
                    Note
                    {statusModalCommentIsRequired && (
                      <span className="tw:text-stoplight-danger">*</span>
                    )}
                  </Label>
                  <Description>
                    {statusModalCommentIsRequired ? 'Required for this status' : 'Optional'}
                  </Description>
                  <Controller
                    name="comment"
                    control={statusModalControl}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        placeholder="Add a note about this status change (visible to all parties)..."
                        rows={3}
                      />
                    )}
                  />
                  {statusModalErrors.comment && (
                    <FieldValidationErrorMessage message={statusModalErrors.comment.message} />
                  )}
                </Field>
              )}
            </div>
          </Fieldset>
          <DialogActions>
            <Button
              type="button"
              outline
              onClick={handleStatusModalClose}
              disabled={statusModalIsSubmitting || isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="brandgreen0"
              disabled={
                statusModalIsSubmitting || isPending || statusModalReasonRequiredButUnavailable
              }
            >
              {statusModalIsSubmitting || isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </DialogBody>
    </Dialog>
  );
}
